import "../node_modules/modern-normalize/modern-normalize.css";
import "./styles/index.scss";

// Core components
import "./components/HeaderNav";
import "./components/SmartHeader";
import "./components/DashedBorderAnimation";
import "./components/MenuImageHover";
import "./components/ButtonHover";
import "./components/FaqAccordion";
import "./components/team-modal";
import "./components/GalleryPage";
import "./components/TimelineScrollJacking";
import "./components/ParallaxImages";
import "./components/SplitTextReveal";
import "./components/FadeInOnScroll";
import "./components/SplitTextReveal";
import "./components/stats-counter";
import "./components/CustomDropdown";
import "./components/ContactForm";
import "./components/ScrollRevealParallax";
import "./components/EventsLoadMore";
import "./components/FooterMenuAnimation";
import HorizontalScroll from "./components/HorizontalScroll";
import HorizontalScrollAnimations from "./components/HorizontalScrollAnimations";
import PageTransition from "./components/PageTransition";
import TransitionConfig from "./utils/TransitionConfig";
import { markPageReady, runWhenPageReady } from "./utils/PageLifecycle";

// Initialize animations
import { initNeighborhoodAnimation } from "./components/neighborhood-animation";
import { initHistoryConnectorsAnimation } from "./components/history-connectors-animation";

// Initialize Gallery components
import Gallery from "./components/Gallery";
import ClubRoomCarousel from "./components/ClubRoomCarousel";
import RoomCardCarousel from "./components/RoomCardCarousel";
import SingleRoomCarousel from "./components/SingleRoomCarousel";
import OffersCarousel from "./components/OffersCarousel";
import RoomsCarousel from "./components/RoomsCarousel";
import HeroGalleryCarousel from "./components/HeroGalleryCarousel";
import HeroGalleryAnimation from "./components/HeroGalleryAnimation";

function initializePageTransition() {
  const transitionConfig = new TransitionConfig();
  
  if (transitionConfig.shouldEnableTransitions()) {
    console.log('Page transitions enabled');
    new PageTransition();
  } else {
    console.log('Page transitions disabled (development mode)');
    const overlay = document.querySelector('.page-transition-overlay');
    if (overlay) {
      overlay.classList.add('is-hidden');
      overlay.style.transform = 'translateY(100%)';
    }
    markPageReady('transitions-disabled');
  }
  
  // Initialize dev tools in development
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
    transitionConfig.debugMode = true;
    transitionConfig.initDevTools();
    console.log('🛠️ Dev tools available: toggleTransitions(), checkTransitions()');
  }
}

// Auto-initialize Gallery and Club Room Carousel components
function initializeComponents() {
  console.log('Initializing components');

  // Mapbox is only used by the map embed block. Loading it on every page adds
  // a large library to the initial JavaScript payload even when no map exists.
  // The module retains its existing auto-initialization once its block is
  // present, so the map's behavior is unchanged on pages that use it.
  if (document.querySelector('.map-embed__map-container')) {
    import('./components/LeafletMap');
  }
  
  // Initialize neighborhood animation
  initNeighborhoodAnimation();
  
  // Initialize history connectors animation
  initHistoryConnectorsAnimation();
  
  const galleryElements = document.querySelectorAll('[data-component="Gallery"]');
  console.log('Found Gallery elements:', galleryElements.length);
  galleryElements.forEach(element => new Gallery(element));
  
  const clubRoomCarousels = document.querySelectorAll('[data-club-room="carousel"]');
  console.log('Found Club Room carousels:', clubRoomCarousels.length);
  clubRoomCarousels.forEach(element => {
    console.log('Initializing Club Room carousel', element);
    new ClubRoomCarousel(element);
  });
  
  const roomCardCarousels = document.querySelectorAll('[data-room-card="carousel"]');
  console.log('Found Room Card carousels:', roomCardCarousels.length);
  roomCardCarousels.forEach(element => {
    console.log('Initializing Room Card carousel', element);
    new RoomCardCarousel(element);
  });
  
  const singleRoomCarousels = document.querySelectorAll('[data-single-room-carousel-container]');
  console.log('Found Single Room carousels:', singleRoomCarousels.length);
  singleRoomCarousels.forEach(element => {
    console.log('Initializing Single Room carousel', element);
    new SingleRoomCarousel(element);
  });
  
  const offersCarousels = document.querySelectorAll('[data-component="OffersCarousel"]');
  console.log('Found Offers carousels:', offersCarousels.length);
  offersCarousels.forEach(element => {
    console.log('Initializing Offers carousel', element);
    new OffersCarousel(element);
  });
  
  const roomsCarousels = document.querySelectorAll('[data-component="RoomsCarousel"]');
  console.log('Found Rooms carousels:', roomsCarousels.length);
  roomsCarousels.forEach(element => {
    console.log('Initializing Rooms carousel', element);
    new RoomsCarousel(element);
  });

  const horizontalScrolls = document.querySelectorAll('[data-component="HorizontalScroll"]');
  console.log('Found Horizontal Scroll sections:', horizontalScrolls.length);
  horizontalScrolls.forEach(element => {
    console.log('Initializing Horizontal Scroll', element);
    new HorizontalScroll(element);
  });
  
  // Initialize horizontal scroll animations after the main scroll is set up
  if (horizontalScrolls.length > 0) {
    new HorizontalScrollAnimations();
  }
}

// Handle both cases: DOM already loaded (defer) or not yet loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePageTransition);
} else {
  // DOM already loaded (script is deferred)
  initializePageTransition();
}

runWhenPageReady(initializeComponents);

// Auto-import all component, pattern, and page SCSS files
require.context("@/components", true, /\.scss$/);
require.context("@/patterns", true, /\.scss$/);
require.context("@/pages", true, /\.scss$/);

// Define which components should be loaded as heavy (code-split)
const HEAVY_COMPONENTS = ["Button", "Maze", "OrbitControls"];

async function loadHeavyComponents(elements) {
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const componentId = element.getAttribute(`data-component`);

    try {
      const module = await import(`./components/${componentId}`);
      const Component = module.default || module[componentId];
      if (typeof Component === `function`) {
        Component(element);
      }
    } catch (error) {
      console.warn(`Component ${componentId} not found`);
    }

    // Stagger loading to prevent blocking
    if (i < elements.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }
}

const main = async () => {
  if (typeof document === `undefined` || typeof window === `undefined`) {
    return;
  }

  runWhenPageReady(async () => {
    const elements = document.querySelectorAll(`[data-component]`);

    const heavyComponents = [];

    // Most theme components are initialized explicitly above. Keep this loader
    // reserved for intentionally code-split heavy modules.
    elements.forEach((element) => {
      const componentId = element.getAttribute(`data-component`);
      if (HEAVY_COMPONENTS.includes(componentId)) {
        heavyComponents.push(element);
      }
    });

    // Load heavy components with delay
    if (heavyComponents.length > 0) {
      setTimeout(() => {
        loadHeavyComponents(heavyComponents);
      }, 1000);
    }
  });
};

main();
